import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { walldata } from '../Config/artprintsabstractdata.js';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import Scrollfunctions from '../Components/Scrollfunctions';
import Shareooptions from '../Components/Shareooptions.js';
import TopSuggestions from '../Components/TopSuggestions.js';
import OurInformation from '../Components/OurInformation.js';
import { Undo2 } from 'lucide-react';
import CollapsibleInfo from '../Components/collapseartprints.js';
import useMetaTags from '../Components/useMetaTags.js';
import watermark from '../Images/watermark/WATERMARK-2.png';
import AddToWishlistButton from '../Components/AddToWishlistButton.js';

const ArtprintsAbstractdesc = () => {
  
  const printSizes = useMemo(() => ({
    'Art Paper': {
      Horizontal: {
        '18 x 12': 1100,
        '24 x 16': 1900,
        '30 x 20': 3100,
        '36 x 24': 4500,
        '42 x 30': 6500,
        '48 x 36': 9000,
        '60 x 40': 12300,
        '72 x 48': 18000,
      },
      Square: {
        '16 x 16': 1300,
        '20 x 20': 2000,
        '24 x 24': 3000,
        '30 x 30': 4600,
        '36 x 36': 6700,
        '40 x 40': 8300,
        '48 x 48': 12000,
        '60 x 60': 18700,
      },
      Vertical: {
        '12 x 18': 1100,
        '16 x 24': 1900,
        '20 x 30': 3100,
        '24 x 36': 4500,
        '30 x 42': 6500,
        '36 x 48': 9000,
        '40 x 60': 12300,
        '48 x 72': 18000,
      },
    },
    Canvas: {
      Horizontal: {
        '18 x 12': 1400,
        '24 x 16': 2500,
        '30 x 20': 4000,
        '36 x 24': 5800,
        '42 x 30': 8400,
        '48 x 36': 11700,
        '60 x 40': 16000,
        '72 x 48': 23400,
      },
      Square: {
        '16 x 16': 1700,
        '20 x 20': 2600,
        '24 x 24': 3900,
        '30 x 30': 6000,
        '36 x 36': 8700,
        '40 x 40': 10800,
        '48 x 48': 15600,
        '60 x 60': 24000,
      },
      Vertical: {
        '12 x 18': 1400,
        '16 x 24': 2500,
        '20 x 30': 4000,
        '24 x 36': 5800,
        '30 x 42': 8400,
        '36 x 48': 11700,
        '40 x 60': 16000,
        '48 x 72': 23400,
      },
    },
  }), []);

  
  const customPricingRates = {
    'Art Paper': 5.15,
    'Canvas': 6.60,
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const imgRef = useRef(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  
  useEffect(() => {
    console.log('[artprintsabstractdesc] Component mounted/updated with id:', id);
    
    // Store the current image ID when component mounts or id changes
    console.log('[artprintsabstractdesc] Storing id in sessionStorage:', id);
    sessionStorage.setItem('lastViewedImageIdArtprintsabstract', id);
    console.log('[artprintsabstractdesc] sessionStorage now contains:', sessionStorage.getItem('lastViewedImageIdArtprintsabstract'));

    const handleBeforeUnload = () => {
      // Store the current image ID in sessionStorage for back navigation
      console.log('[artprintsabstractdesc] Before unload - storing id:', id);
      sessionStorage.setItem('lastViewedImageIdArtprintsabstract', id);
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup: Store ID when component unmounts (e.g., when navigating away)
    return () => {
      console.log('[artprintsabstractdesc] Component unmounting - storing id:', id);
      sessionStorage.setItem('lastViewedImageIdArtprintsabstract', id);
      console.log('[artprintsabstractdesc] After unmount, sessionStorage contains:', sessionStorage.getItem('lastViewedImageIdArtprintsabstract'));
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [id]);

  const selectedImage = React.useMemo(() => {
    const image = walldata[id - 1];
    console.log('[abstractdesc] selectedImage calculated - id:', id, 'image:', image);
    return image;
  }, [id]);

  
  useMetaTags({
    title: selectedImage ? `${selectedImage.title} - Abstract Art Print | Vizaura` : 'Abstract Art Print | Vizaura',
    description: selectedImage ? `${selectedImage.title} - ${selectedImage.desc}. ${selectedImage.context}` : 'Discover beautiful abstract art prints from Vizaura. Premium wallpapers, art prints, hand paintings, and custom interior graphics tailored for your space.',
    image: selectedImage ? selectedImage.Img : 'https://artfluence2.vercel.app/Images/vizaura_logo.png',
    url: window.location.href,
    type: 'website'
  });

  const [isWide, setIsWide] = useState(window.innerWidth >= 1077);

  const [selectedPrintMaterial, setSelectedPrintMaterial] = useState('Art Paper');
  const [selectedPrintSize, setSelectedPrintSize] = useState('');
  const [selectedPrintOrientation, setSelectedPrintOrientation] = useState('Horizontal');
  const [presetPrice, setPresetPrice] = useState(null);

  const [height, setHeight] = useState('');
  const [width, setWidth] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);

  const [showCustomOptions, setShowCustomOptions] = useState(false);

  const updateUrlParams = ({ material, orientation, size, customWidth, customHeight, isCustom }) => {
    const params = new URLSearchParams();
    if (isCustom) {
      params.set('mode', 'custom');
      if (material) params.set('material', material);
      if (customWidth) params.set('width', customWidth);
      if (customHeight) params.set('height', customHeight);
    } else {
      params.set('mode', 'preset');
      if (material) params.set('material', material);
      if (orientation) params.set('orientation', orientation);
      if (size) params.set('size', size);
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlMode = params.get('mode');
    const urlMaterial = params.get('material');
    const urlOrientation = params.get('orientation');
    const urlSize = params.get('size');
    const urlWidth = params.get('width');
    const urlHeight = params.get('height');

    if (urlMode === 'custom') {
      setShowCustomOptions(true);
      if (urlMaterial && Object.keys(printSizes).includes(urlMaterial)) {
        setSelectedPrintMaterial(urlMaterial);
      }
      if (urlWidth) setWidth(urlWidth);
      if (urlHeight) setHeight(urlHeight);
    } else {
      setShowCustomOptions(false);
      if (urlMaterial && Object.keys(printSizes).includes(urlMaterial)) {
        setSelectedPrintMaterial(urlMaterial);
      }
      if (urlOrientation && Object.keys(printSizes[selectedPrintMaterial] || {}).includes(urlOrientation)) {
        setSelectedPrintOrientation(urlOrientation);
      }
      if (urlSize && printSizes[selectedPrintMaterial]?.[urlOrientation || 'Horizontal']?.[urlSize]) {
        setSelectedPrintSize(urlSize);
      }
    }
  }, [location.search, selectedPrintMaterial, printSizes]);

  useEffect(() => {
    const handleResize = () => {
      setIsWide(window.innerWidth >= 1077);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!showCustomOptions && selectedPrintMaterial && selectedPrintOrientation && selectedPrintSize) {
      const price = printSizes[selectedPrintMaterial]?.[selectedPrintOrientation]?.[selectedPrintSize];
      setPresetPrice(price || null);
    } else {
      setPresetPrice(null);
    }
  }, [selectedPrintMaterial, selectedPrintOrientation, selectedPrintSize, showCustomOptions, printSizes]);

  const handlePrintMaterialChange = (material) => {
    setSelectedPrintMaterial(material);
    if (showCustomOptions) {
      setCalculatedPrice(null);
      updateUrlParams({ material, customWidth: width, customHeight: height, isCustom: true });
    } else {
      setSelectedPrintSize('');
      setSelectedPrintOrientation('Horizontal');
      setPresetPrice(null);
      updateUrlParams({ material, orientation: 'Horizontal', size: '', isCustom: false });
    }
  };

  const handlePrintSizeChange = (size) => {
    setSelectedPrintSize(size);
    updateUrlParams({ material: selectedPrintMaterial, orientation: selectedPrintOrientation, size, isCustom: false });
  };

  const handlePrintOrientationChange = (orientation) => {
    setSelectedPrintOrientation(orientation);
    setSelectedPrintSize('');
    setPresetPrice(null);
    updateUrlParams({ material: selectedPrintMaterial, orientation, size: '', isCustom: false });
  };

  const handleHeightChange = (e) => {
    const newHeight = e.target.value;
    setHeight(newHeight);
    setCalculatedPrice(null);
    updateUrlParams({ material: selectedPrintMaterial, customWidth: width, customHeight: newHeight, isCustom: true });
  };

  const handleWidthChange = (e) => {
    const newWidth = e.target.value;
    setWidth(newWidth);
    setCalculatedPrice(null);
    updateUrlParams({ material: selectedPrintMaterial, customWidth: newWidth, customHeight: height, isCustom: true });
  };

  const calculatePrice = () => {
    const parsedWidth = parseFloat(width);
    const parsedHeight = parseFloat(height);

    if (isNaN(parsedWidth) || isNaN(parsedHeight) || parsedWidth <= 0 || parsedHeight <= 0) {
      alert('Please enter valid positive numbers for both width and height.');
      setCalculatedPrice(null);
      return;
    }

    const pricePerSqInch = customPricingRates[selectedPrintMaterial];
    if (!pricePerSqInch) {
      alert('Pricing data for the selected material is missing for custom sizes.');
      setCalculatedPrice(null);
      return;
    }

    const totalArea = parsedWidth * parsedHeight;
    const totalPrice = totalArea * pricePerSqInch;
    setCalculatedPrice(Math.floor(totalPrice));
  };

  const toggleCustomOptions = () => {
    const newShowCustomOptions = !showCustomOptions;
    setShowCustomOptions(newShowCustomOptions);

    if (newShowCustomOptions) {
      setSelectedPrintSize('');
      setSelectedPrintOrientation('Horizontal');
      setPresetPrice(null);
      updateUrlParams({ material: selectedPrintMaterial, customWidth: width, customHeight: height, isCustom: true });
    } else {
      setHeight('');
      setWidth('');
      setCalculatedPrice(null);
      updateUrlParams({ material: selectedPrintMaterial, orientation: selectedPrintOrientation, size: selectedPrintSize, isCustom: false });
    }
  };

  const handleMouseMove = (e) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;
    img.style.transformOrigin = `${xPercent}% ${yPercent}%`;
    img.style.transform = 'scale(1.5)';
  };

  const handleMouseLeave = () => {
    const img = imgRef.current;
    if (!img) return;
    img.style.transformOrigin = 'center';
    img.style.transform = 'scale(1)';
  };

  function useWindowWidth() {
    const [widths, setWidths] = useState(window.innerWidth);
    useEffect(() => {
      const handleResize = () => setWidths(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    return widths;
  }

  const widths = useWindowWidth();
  const isMobileOrTablet = widths <= 1023;

  if (!selectedImage) {
    return navigate('/404');
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />
      <div className="container mx-auto pt-20 px-4 sm:px-6 lg:px-8">
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => {
              console.log('[artprintsabstractdesc] Back button clicked - current id:', id);
              // Store the current ID before navigating (in case of refresh scenario)
              sessionStorage.setItem('lastViewedImageIdArtprintsabstract', id);
              console.log('[artprintsabstractdesc] Stored id in sessionStorage before navigation:', id);
              // Navigate with scrollTo parameter in URL
              const targetUrl = `/artprints/abstract?scrollTo=${id}`;
              console.log('[artprintsabstractdesc] Navigating to:', targetUrl);
              navigate(targetUrl);
            }}
            className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700
            group hover:ring-2 hover:ring-gray-700 hover:ring-opacity-50 shadow-md hover:shadow-xl
            flex items-center justify-center transition-all duration-300"
            aria-label="Go Back"
          >
            <Undo2 className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
          </button>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 ${isWide ? 'gap-12' : 'gap-6'} mb-16 items-start`}>
          <div className="relative overflow-hidden rounded-xl shadow-xl shadow-gray-400">
            <div className="absolute bottom-4 right-4 flex items-center space-x-2 z-10 pointer-events-none">
              <span className="text-lg lg:text-2xl text-gray-200" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.4)' }}>©</span>
              <img
                src={watermark}
                alt="Watermark"
                className="w-24 lg:w-48 select-none"
              />
            </div>

            <img
              ref={imgRef}
              src={selectedImage.Img}
              alt={selectedImage.title}
              className={
                isMobileOrTablet
                  ? "w-full h-auto object-cover transition-transform duration-300"
                  : "w-full h-auto object-contain transition-transform duration-300"
              }
              {...(!isMobileOrTablet && {
                onMouseMove: handleMouseMove,
                onMouseLeave: handleMouseLeave,
              })}
              style={{
                transition: 'transform 0.4s ease-out, transform-origin 0.4s ease-out',
              }}
              onError={(e) => {
                const el = e.currentTarget;
                if (el.src.endsWith('.jpg')) {
                  el.src = el.src.replace('.jpg', '.jpeg');
                }
              }}
            />
          </div>
          <div className="flex flex-col h-full space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-lg relative">
              <div className="absolute top-4 right-4">
                {selectedImage && (
                  <AddToWishlistButton 
                    item={selectedImage} 
                    selections={{
                      material: selectedPrintMaterial,
                      orientation: selectedPrintOrientation,
                      size: selectedPrintSize,
                      isCustom: showCustomOptions,
                      customWidth: showCustomOptions ? width : null,
                      customHeight: showCustomOptions ? height : null,
                      price: presetPrice || calculatedPrice
                    }}
                  />
                )}
              </div>
              <h1 className="text-xl font-serif text-gray-800 mb-2 pr-12">{selectedImage.desc}</h1>
              <p className="text-base text-gray-600 mb-4 leading-relaxed font-medium">{selectedImage.title}</p>
              <p className="text-sm text-gray-500 leading-relaxed">{selectedImage.context}</p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-lg space-y-4">
              <div className="flex justify-center mb-6">
                <div className="inline-flex bg-gray-100 p-2 rounded-xl shadow-sm">
                  <button
                    onClick={() => toggleCustomOptions()}
                    className={`px-8 py-3 rounded-lg text-base font-semibold transition-all duration-200 mx-1
                      ${!showCustomOptions
                        ? 'bg-gray-600 text-white shadow-md transform scale-105'
                        : 'bg-gray-300 text-gray-600 hover:bg-gray-400 hover:scale-102'
                      }`}
                  >
                    Standard Sizes
                  </button>
                  <button
                    onClick={() => toggleCustomOptions()}
                    className={`px-8 py-3 rounded-lg text-base font-semibold transition-all duration-200 mx-3
                      ${showCustomOptions
                        ? 'bg-gray-600 text-white shadow-md transform scale-105'
                        : 'bg-gray-300 text-gray-600 hover:bg-gray-400 hover:scale-102'
                      }`}
                  >
                    Custom Size
                  </button>
                </div>
              </div>
              <label className="block font-semibold text-sm text-gray-800">Select Print Material</label>
              <div className="flex flex-wrap gap-2 mb-4 md:flex hidden"> 
                {Object.keys(printSizes).map((material) => (
                  <button
                    key={material}
                    onClick={() => handlePrintMaterialChange(material)}
                    className={`
                      px-5 py-2 rounded-xl border-2 text-sm font-semibold whitespace-nowrap
                      transition-all duration-300
                      ${selectedPrintMaterial === material
                        ? 'bg-gray-600 text-white border-gray-700 shadow-md'
                        : 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300'
                      }
                    `}
                  >
                    {material}
                  </button>
                ))}
              </div>
              <div className="md:hidden grid grid-cols-3 gap-2 mb-4">
                {Object.keys(printSizes).map((material) => (
                  <button
                    key={material}
                    onClick={() => handlePrintMaterialChange(material)}
                    className={`
                      px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap text-center
                      transition-all duration-300
                      ${selectedPrintMaterial === material
                        ? 'bg-gray-600 text-white border-gray-700 shadow-md'
                        : 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300'
                      }
                    `}
                  >
                    {material}
                  </button>
                ))}
              </div>


              {!showCustomOptions ? (
                <div className="space-y-4">
                  <label className="block font-semibold text-sm text-gray-800">Select Orientation</label>
                  <div className="flex flex-wrap gap-2 mb-4 md:flex hidden"> 
                    {selectedPrintMaterial && Object.keys(printSizes[selectedPrintMaterial] || {}).map((orientation) => (
                      <button
                        key={orientation}
                        onClick={() => handlePrintOrientationChange(orientation)}
                        className={`
                          px-5 py-2 rounded-xl border-2 text-sm font-semibold whitespace-nowrap
                          transition-all duration-300
                          ${selectedPrintOrientation === orientation
                            ? 'bg-gray-600 text-white border-gray-700 shadow-md'
                            : 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300'
                          }
                        `}
                      >
                        {orientation}
                      </button>
                    ))}
                  </div>
                  <div className="md:hidden grid grid-cols-3 gap-2 mb-4">
                    {selectedPrintMaterial && Object.keys(printSizes[selectedPrintMaterial] || {}).map((orientation) => (
                      <button
                        key={orientation}
                        onClick={() => handlePrintOrientationChange(orientation)}
                        className={`
                          px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap text-center
                          transition-all duration-300
                          ${selectedPrintOrientation === orientation
                            ? 'bg-gray-600 text-white border-gray-700 shadow-md'
                            : 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300'
                          }
                        `}
                      >
                        {orientation}
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="block font-semibold mb-2 text-sm text-gray-800">Select Size (Width x Height in inches)</label>
                    <div className="flex flex-wrap gap-2 md:flex hidden"> 
                      {selectedPrintMaterial && selectedPrintOrientation &&
                        Object.keys(printSizes[selectedPrintMaterial][selectedPrintOrientation]).map((size) => (
                          <button
                            key={size}
                            onClick={() => handlePrintSizeChange(size)}
                            className={`
                              px-4 py-2 rounded-lg border text-xs font-medium whitespace-nowrap
                              transition-all duration-300
                              ${selectedPrintSize === size
                                ? 'bg-gray-600 text-white border-gray-700 shadow-sm'
                                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                              }
                            `}
                          >
                            {size}
                          </button>
                        ))}
                    </div>
                    <div className="md:hidden grid grid-cols-3 gap-2">
                      {selectedPrintMaterial && selectedPrintOrientation &&
                        Object.keys(printSizes[selectedPrintMaterial][selectedPrintOrientation]).map((size) => (
                          <button
                            key={size}
                            onClick={() => handlePrintSizeChange(size)}
                            className={`
                              px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap text-center
                              transition-all duration-300
                              ${selectedPrintSize === size
                                ? 'bg-gray-600 text-white border-gray-700 shadow-sm'
                                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                              }
                            `}
                          >
                            {size}
                          </button>
                        ))}
                    </div>
                  </div>

                  {presetPrice !== null && (
                    <div className="p-3 bg-gray-50 border border-gray-100 rounded-lg mt-4 text-center"> 
                      <p className="text-sm font-semibold text-gray-700 mb-2">Selected Size: {selectedPrintSize} inches</p>
                      <div className="text-center">
                        <span className="text-base font-bold text-gray-700">Total Price: </span>
                        <span className="text-2xl font-extrabold text-black-800">₹ {presetPrice.toLocaleString('en-IN')}</span> 
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-semibold mb-1 text-xs text-gray-700">Width (inches)</label>
                      <input
                        type="number"
                        value={width}
                        onChange={handleWidthChange}
                        placeholder="Width in inches"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition duration-300 outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1 text-xs text-gray-700">Height (inches)</label>
                      <input
                        type="number"
                        value={height}
                        onChange={handleHeightChange}
                        placeholder="Height in inches"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition duration-300 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <button
                    onClick={calculatePrice}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 text-sm"
                  >
                    Calculate Price
                  </button>

                  {calculatedPrice !== null && (
                    <div className="p-3 bg-gray-50 border border-gray-100 rounded-lg text-center"> 
                      <p className="text-sm font-semibold text-gray-700 mb-2">Custom Size: {width}" × {height}"</p>
                      <div className="text-center">
                        <span className="text-base font-bold text-gray-700">Estimated Price: </span>
                        <span className="text-2xl font-extrabold text-gray-800">₹ {parseFloat(calculatedPrice).toLocaleString('en-IN')}</span> 
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col items-center">
              <Shareooptions />
            </div>
            <CollapsibleInfo />
          </div>
        </div>

        <TopSuggestions />
        <Scrollfunctions />
        <OurInformation />
      </div>
      <Footer />
    </div>
  );
};

export default ArtprintsAbstractdesc;